(() => {
  function labelHasMappedVisual(label) {
    if (label.querySelector('img, .globo-swatch-image')) return true;
    const style = label.getAttribute('style') || '';
    if (/background-image\s*:/i.test(style)) return true;
    if (/background-color\s*:/i.test(style)) return true;
    return false;
  }

  function isDropdown(group) {
    return Boolean(
      group.querySelector('.ul-globo-dropdown-option, select, .dropdown-selector, .dropdown-button-gsw')
    );
  }

  function isButton(group) {
    return Boolean(group.querySelector('.globo-style--button'));
  }

  function optionNameFrom(node) {
    const labelled = node.querySelector('.form__label, legend, .name-option');
    const raw = labelled?.getAttribute('data-name') || labelled?.childNodes[0]?.textContent || labelled?.textContent || '';
    return raw.split(':')[0].trim().toLowerCase();
  }

  function hideUnmappedImageGroups(info) {
    if (info.hasAttribute('data-joias-botoes-product')) return;

    const covered = new Set();

    info.querySelectorAll('.swatch--gl').forEach((group) => {
      const name = (group.querySelector('.name-option')?.getAttribute('data-name') || '').trim().toLowerCase();
      const mapped = isDropdown(group) || isButton(group) || [...group.querySelectorAll('.swatch-anchor')].some(labelHasMappedVisual);

      if (!mapped) {
        group.hidden = true;
        return;
      }

      group.hidden = false;
      if (name) covered.add(name);
    });

    info.querySelectorAll('variant-selects .product-form__input').forEach((input) => {
      const name = optionNameFrom(input);
      input.hidden = Boolean(name && covered.has(name));
    });

    info.querySelectorAll('variant-selects').forEach((selects) => {
      const hasVisible = [...selects.querySelectorAll('.product-form__input')].some((input) => !input.hidden);
      selects.hidden = !hasVisible;
    });
  }

  const MONTHS = /^(janeiro|fevereiro|março|marco|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro)\b/i;

  // Gem photos by month (the same files the extra pendants of the children's necklace use).
  const GEM_BASE = '/cdn/shop/files/';
  const GEM_IMAGES = Object.fromEntries(
    Object.entries({
      janeiro: '17849897614547',
      fevereiro: '17849897720506',
      marco: '17849897797029',
      abril: '17849897870639',
      maio: '17849898084005',
      junho: '17849898164616',
      julho: '17849898240551',
      agosto: '17849898323716',
      setembro: '17849914772445',
      outubro: '17849898959472',
      novembro: '17849899036534',
      dezembro: '17849899119046',
    }).map(([month, id]) => [month, `${GEM_BASE}swatch-${id}.png?width=160`])
  );

  // Birthstone options: the app draws a bare circle per month, so show the month under each one.
  function addStoneMonthLabels(info) {
    info.querySelectorAll('.swatch--gl').forEach((group) => {
      const name = group.querySelector('.name-option')?.getAttribute('data-name') || '';
      // "Mês" of the children's necklace: text buttons turned into the same gem photos as its extra pendants.
      const useGemImages = /^m[êe]s$/i.test(name.trim()) && isButton(group);
      // Other text buttons and dropdowns already spell the month out.
      if (!useGemImages && (!/pedra|stone/i.test(name) || isButton(group) || isDropdown(group))) return;

      let hasMonths = false;
      group.querySelectorAll('li.select-option').forEach((item) => {
        const value = item.getAttribute('data-value') || '';
        const month = value.split(/\s*[—–-]\s*/)[0].trim();
        if (!MONTHS.test(month)) return;

        hasMonths = true;
        if (useGemImages) {
          const gem = GEM_IMAGES[month.toLowerCase().replace('ç', 'c')];
          if (gem) item.style.setProperty('--eva-stone-image', `url("${gem}")`);
        }
        if (item.querySelector('.eva-stone-month')) return;
        const input = item.querySelector('input');
        const label = document.createElement(input && input.id ? 'label' : 'span');
        label.className = 'eva-stone-month';
        label.textContent = month;
        if (input && input.id) label.htmlFor = input.id;
        item.appendChild(label);
      });
      group.classList.toggle('eva-stone-months', hasMonths);
      group.classList.toggle('eva-stone-images', hasMonths && useGemImages);
    });
  }

  // The server sends the theme's own option pickers; the Globo app replaces them a moment later. They stay
  // invisible (CSS, see section-main-product-impulse.css) until the final pickers are in place, so the shopper
  // never sees the first version. If the app does not show up, the theme's pickers are shown after a short wait.
  const VARIANTS_WAIT = 1800;

  function markVariantsReady(info) {
    if (info.dataset.evaVariants === 'ready') return;
    if (info.querySelector('.swatch--gl')) {
      info.dataset.evaVariants = 'ready';
      return;
    }
    if (!info._evaVariantsTimer) {
      info._evaVariantsTimer = setTimeout(() => {
        info.dataset.evaVariants = 'ready';
      }, VARIANTS_WAIT);
    }
  }

  // Extra pendants of the children's necklace (Global Product Options): the month under each gem photo.
  function addGpoMonthLabels(info) {
    info.querySelectorAll('.gpo-swatches.image-swatches').forEach((item) => {
      const input = item.querySelector('input');
      if (!input || item.querySelector('.eva-gpo-month')) return;
      const month = (input.value || '').split(/\s*[—–-]\s*/)[0].trim();
      if (!MONTHS.test(month)) return;

      const label = document.createElement(input.id ? 'label' : 'span');
      label.className = 'eva-gpo-month';
      label.textContent = month;
      if (input.id) label.htmlFor = input.id;
      item.appendChild(label);
    });
  }

  function scanInfo(info) {
    hideUnmappedImageGroups(info);
    addStoneMonthLabels(info);
    addGpoMonthLabels(info);
    markVariantsReady(info);
  }

  // Changes that need an answer before the next paint: the first version of an option picker would show.
  const URGENT = 'variant-selects, .product-form__input, .swatch--gl';

  function hasUrgentNodes(records) {
    return records.some((record) =>
      [...record.addedNodes].some(
        (node) => node.nodeType === 1 && (node.matches(URGENT) || node.querySelector(URGENT))
      )
    );
  }

  const idle = (callback) =>
    typeof requestIdleCallback === 'function' ? requestIdleCallback(callback, { timeout: 300 }) : setTimeout(callback, 80);

  // One observer per product block (nothing is observed on pages without one), and only for changes that matter.
  function watch(info) {
    if (info._evaWatched) return;
    info._evaWatched = true;
    scanInfo(info);

    let pending = false;
    const run = () => {
      if (!pending) return;
      pending = false;
      scanInfo(info);
    };
    const observer = new MutationObserver((records) => {
      const watcher = window.EvaProductWatch;
      if (watcher && !watcher.isRelevant(records)) return;
      if (pending) return;
      pending = true;
      if (info.dataset.evaVariants !== 'ready' || hasUrgentNodes(records)) {
        // Before the next paint, so a re-rendered picker is never drawn in its first version. The timeout is
        // only there for tabs where animation frames are paused.
        requestAnimationFrame(run);
        setTimeout(run, 100);
      } else {
        // Anything else can wait until the browser has answered the tap.
        idle(run);
      }
    });
    observer.observe(info, { childList: true, subtree: true });
  }

  function start() {
    document.querySelectorAll('product-info').forEach(watch);
    // Product blocks that connect later (product-info.js sends this event when one does).
    document.addEventListener('product-info:loaded', (event) => {
      if (event.target && event.target.matches && event.target.matches('product-info')) watch(event.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
